import React from "react";
import { useBirthdayValidation } from "./booking/validation/useBirthdayValidation";
import { useBookingFormInit } from "./booking/useBookingFormInit";
import { useBookingSubmission } from "./booking/useBookingSubmission";
import { TimeSlot, BookingLanguage } from "@/types/booking"; // Add BookingLanguage import
import { FormValues } from "@/components/booking-form/types";
import { Product } from "@/components/product-step/types";

interface UseBookingFormProps {
  email: string;
  selectedTimeSlots: TimeSlot[];
  bookingType: "request";
  selectedProducts: string[];
  selectedAddOns?: string[]; // Add selectedAddOns prop
  onSubmit: (data: Record<string, unknown>) => void;
  bookingId: string;
  language: BookingLanguage;
  /** Flow marker: "membership-first" for book.aheadhealth.com, absent otherwise. */
  flow?: string;
  discount?: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
  selectedClinic: string;
  packageComponentMap?: Record<string, string[]>;
  products?: Product[]; // Add products for GA4 tracking
  totalPrice?: number; // Add totalPrice prop
}

export const useBookingForm = ({
  email,
  selectedTimeSlots,
  selectedProducts,
  selectedAddOns = [], // Add selectedAddOns with default empty array
  onSubmit,
  bookingId,
  language,
  flow,
  discount,
  selectedClinic,
  packageComponentMap = {}, // Default to empty object
  products = [], // Add products with default empty array
  totalPrice,
}: UseBookingFormProps) => {
  console.log("[useBookingForm] Hook called");

  // Initialize the form using the dedicated hook
  const form = useBookingFormInit({ email });

  console.log("[useBookingForm] Form state after init:", {
    submitCount: form.formState.submitCount,
    isSubmitted: form.formState.isSubmitted,
  });
  const {
    register,
    control,
    formState: { errors },
    setError,
  } = form;

  // Use the birthday validation hook
  const { validateBirthday } = useBirthdayValidation(setError);

  // Use the submission handler hook
  const { handleFormSubmit, buildBookingPayload } = useBookingSubmission({
    bookingId,
    selectedTimeSlots,
    selectedProducts,
    selectedAddOns, // Pass selectedAddOns to useBookingSubmission
    selectedClinic,
    language,
    flow,
    discount,
    email,
    onSubmitSuccess: onSubmit,
    packageComponentMap, // Pass packageComponentMap to useBookingSubmission
    products, // Pass products for GA4 tracking
    totalPrice, // Pass totalPrice to useBookingSubmission
  });

  // Track which render created which handler
  const handlerIdRef = React.useRef(0);
  handlerIdRef.current++;
  const handlerId = handlerIdRef.current;

  console.log(
    "[useBookingForm] Creating handlers, handlerId:",
    handlerId,
    "submitCount:",
    form.formState.submitCount,
  );

  // Wrapper to ensure formData is passed to our submission handler
  // Note: form.handleSubmit returns a function that, when called, will:
  // 1. Increment submitCount
  // 2. Validate the form
  // 3. If valid, call our callback
  const innerSubmitHandler = form.handleSubmit(
    // onValid callback
    (formData: FormValues) => {
      console.log("[useBookingForm] Form VALID - handlerId:", handlerId);
      handleFormSubmit(formData);
    },
    // onInvalid callback - called when validation fails
    (errors) => {
      console.log(
        "[useBookingForm] Form INVALID - handlerId:",
        handlerId,
        "errors:",
        JSON.stringify(Object.keys(errors)),
      );
      // Capture the full stack trace as a string
      const stack = new Error().stack;
      console.log("[useBookingForm] Invalid submission FULL STACK:", stack);
    },
  );

  // Wrap the submit handler to log EVERY call to it
  const submitHandler = (e?: React.BaseSyntheticEvent) => {
    const eventInfo = JSON.stringify({
      hasEvent: !!e,
      eventType: e?.type,
      eventTarget: (e?.target as HTMLElement)?.tagName,
      eventTargetId: (e?.target as HTMLElement)?.id,
    });
    console.log(
      "[useBookingForm] submitHandler INVOKED - handlerId:",
      handlerId,
      eventInfo,
    );
    const stack = new Error().stack;
    console.log("[useBookingForm] submitHandler FULL STACK:", stack);
    return innerSubmitHandler(e);
  };

  console.log(
    "[useBookingForm] submitHandler created, handlerId:",
    handlerId,
    "submitCount:",
    form.formState.submitCount,
  );

  return {
    register,
    control,
    errors,
    handleSubmit: submitHandler,
    validateBirthday,
    form,
    buildBookingPayload,
  };
};
